import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck,
  Search,
  MapPin,
  Phone,
  Mail,
  Globe,
  MessageCircle,
  Plus,
  Building2,
  Package,
  CheckCircle,
  Star
} from "lucide-react";
import { SupplierRating } from "@/components/SupplierRating";

const CATEGORIES = [
  { value: "ALL", label: "सभी श्रेणियां" },
  { value: "AGRO BASED", label: "कृषि आधारित" },
  { value: "SERVICE", label: "सेवा उद्योग" },
  { value: "POLYMER AND CHEMICAL", label: "रासायनिक" },
  { value: "MINERAL BASED", label: "खनिज आधारित" },
  { value: "FOREST BASED", label: "वन आधारित" },
  { value: "RURAL ENGINEERING", label: "ग्रामीण इंजीनियरिंग" },
  { value: "TEXTILE", label: "वस्त्र उद्योग" },
];

const SUPPLIER_TYPES = [
  { value: "ALL", label: "सभी टाइप" },
  { value: "Machinery", label: "मशीनरी" },
  { value: "Raw Material", label: "कच्चा माल" },
  { value: "Both", label: "दोनों" },
];

export default function SuppliersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // Filters
  const [category, setCategory] = useState(searchParams.get("category") || "ALL");
  const [supplierType, setSupplierType] = useState("ALL");
  const [city, setCity] = useState("");

  useEffect(() => {
    fetchSuppliers();
  }, [category, supplierType, city]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== "ALL") params.append("category", category);
      if (supplierType && supplierType !== "ALL") params.append("supplier_type", supplierType);
      if (city) params.append("city", city);
      
      const response = await axios.get(`${API}/suppliers?${params.toString()}`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
    setLoading(false);
  };

  const shareSupplier = (supplier) => {
    const message = `📦 *${supplier.business_name_hindi || supplier.business_name}*

🏷️ टाइप: ${supplier.supplier_type}
📍 स्थान: ${supplier.city}, ${supplier.state}
📞 फोन: ${supplier.phone}
📧 ईमेल: ${supplier.email}
${supplier.website ? `🌐 वेबसाइट: ${supplier.website}` : ""}

📝 ${supplier.products_services_hindi || supplier.products_services}

---
*उद्यमी मित्र* से सप्लायर खोजें! 🚀`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
    toast.success("WhatsApp पर शेयर करें");
  };

  const getTypeIcon = (type) => {
    if (type === "Machinery" || type === "मशीनरी") return Building2;
    if (type === "Raw Material" || type === "कच्चा माल") return Package;
    return Truck;
  };

  const getTypeBadgeColor = (type) => {
    if (type === "Machinery" || type === "मशीनरी") return "bg-blue-500/20 text-blue-500 border-blue-500/30";
    if (type === "Raw Material" || type === "कच्चा माल") return "bg-emerald-500/20 text-emerald-500 border-emerald-500/30";
    return "bg-purple-500/20 text-purple-500 border-purple-500/30";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
                सप्लायर डायरेक्टरी
              </h1>
              <p className="text-sm text-gray-8000">
                मशीनरी और कच्चा माल सप्लायर्स खोजें
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => navigate("/supplier-registration")}
            className="btn-primary rounded-sm"
            data-testid="register-supplier-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            सप्लायर रजिस्टर करें
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-gray-8000 mb-1 block">श्रेणी</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="input-industrial" data-testid="category-filter">
                  <SelectValue placeholder="सभी श्रेणियां" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-gray-8000 mb-1 block">सप्लायर टाइप</label>
              <Select value={supplierType} onValueChange={setSupplierType}>
                <SelectTrigger className="input-industrial">
                  <SelectValue placeholder="सभी टाइप" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {SUPPLIER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-gray-8000 mb-1 block">शहर</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-8000" />
                <Input
                  placeholder="शहर का नाम..."
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="input-industrial pl-10"
                  data-testid="city-filter"
                />
              </div>
            </div>
            
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setCategory("ALL");
                  setSupplierType("ALL");
                  setCity("");
                }}
                className="w-full btn-secondary rounded-sm"
              >
                रीसेट करें
              </Button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-8000">
            {loading ? "लोड हो रहा है..." : `${suppliers.length} सप्लायर मिले`}
          </p>
        </div>

        {/* Suppliers Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-48 bg-white" />
            ))}
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Truck className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl text-gray-500 mb-2">कोई सप्लायर नहीं मिला</h3>
            <p className="text-gray-8000 mb-6">
              अपने फ़िल्टर बदलें या नए सप्लायर रजिस्टर करें
            </p>
            <Button
              onClick={() => navigate("/supplier-registration")}
              className="btn-primary rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              सप्लायर रजिस्टर करें
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {suppliers.map((supplier) => {
              const TypeIcon = getTypeIcon(supplier.supplier_type);
              return (
                <div
                  key={supplier.id}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:border-orange-500/50 transition-colors cursor-pointer"
                  data-testid={`supplier-card-${supplier.id}`}
                  onClick={() => setSelectedSupplier(supplier)}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                        <TypeIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-rajdhani font-bold text-lg text-gray-800">
                            {supplier.business_name_hindi || supplier.business_name}
                          </h3>
                          {supplier.is_verified && (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-8000">{supplier.business_name}</p>
                        {/* Rating Display */}
                        {supplier.average_rating > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            <span className="text-yellow-500 font-medium">{supplier.average_rating?.toFixed(1)}</span>
                            <span className="text-gray-8000 text-xs">({supplier.total_ratings})</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge className={`${getTypeBadgeColor(supplier.supplier_type)} border shrink-0`}>
                      {supplier.supplier_type}
                    </Badge>
                  </div>

                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                    {supplier.products_services_hindi || supplier.products_services}
                  </p>

                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {supplier.categories?.slice(0, 3).map((cat) => (
                      <Badge key={cat} className="bg-gray-100 text-gray-500 text-xs">
                        {cat}
                      </Badge>
                    ))}
                    {supplier.categories?.length > 3 && (
                      <Badge className="bg-gray-100 text-gray-500 text-xs">
                        +{supplier.categories.length - 3} और
                      </Badge>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="w-4 h-4 text-slate-600" />
                      {supplier.city}, {supplier.state}
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="w-4 h-4 text-slate-600" />
                      {supplier.phone}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <a
                      href={`tel:${supplier.phone}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-slate-700 rounded text-sm text-gray-600 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      कॉल करें
                    </a>
                    <a
                      href={`mailto:${supplier.email}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-slate-700 rounded text-sm text-gray-600 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      ईमेल
                    </a>
                    <button
                      onClick={() => shareSupplier(supplier)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-sm text-white transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Supplier Detail Dialog */}
        <Dialog open={!!selectedSupplier} onOpenChange={() => setSelectedSupplier(null)}>
          <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedSupplier && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
                    {selectedSupplier.business_name_hindi || selectedSupplier.business_name}
                    {selectedSupplier.is_verified && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <p className="text-gray-500">
                    {selectedSupplier.products_services_hindi || selectedSupplier.products_services}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-8000">मालिक</p>
                      <p className="text-gray-700">{selectedSupplier.owner_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-8000">टाइप</p>
                      <p className="text-gray-700">{selectedSupplier.supplier_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-8000">फोन</p>
                      <p className="text-gray-700">{selectedSupplier.phone}</p>
                    </div>
                    <div>
                      <p className="text-gray-8000">ईमेल</p>
                      <p className="text-gray-700">{selectedSupplier.email}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-8000">स्थान</p>
                      <p className="text-gray-700">{selectedSupplier.city}, {selectedSupplier.state}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {selectedSupplier.categories?.map((cat) => (
                      <Badge key={cat} className="bg-gray-100 text-gray-500">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    <a
                      href={`tel:${selectedSupplier.phone}`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-orange-500 hover:bg-orange-600 rounded text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      कॉल करें
                    </a>
                    <button
                      onClick={() => shareSupplier(selectedSupplier)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 rounded text-white transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </button>
                  </div>
                </div>
                
                {/* Rating Component */}
                <SupplierRating 
                  supplierId={selectedSupplier.id} 
                  supplierName={selectedSupplier.business_name_hindi || selectedSupplier.business_name}
                />
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
